<script setup lang="ts">
import type { RefDataModel } from '@rialtic/types'
import { useDebounceFn } from '@vueuse/core'
import FormFieldCell from './FormFieldCell.vue'
import { useClaimFormContext } from '../composables/fields'
import { useClaimLab } from '../stores/claimLab'
import type { WidgetAttributes } from '../utils/claim-editor'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  name: string
  attributes?: WidgetAttributes
  maxEntries?: number
  refDataModel?: RefDataModel
}>()

const claimLab = useClaimLab()
const { state } = useClaimFormContext()

/* The form value stays a comma-separated string; the menu works on the list */
const entries = computed(() =>
  String(state[props.name] ?? '')
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean),
)
const items = computed(() =>
  props.refDataModel ? claimLab.getAutocompleteItems(props.refDataModel) : [],
)
/* The store tracks loading per ref data model, which every field of that model shares */
const pending = ref(0)
const loading = computed(() => pending.value > 0)

const searchStore = async (term: string) => {
  pending.value++

  try {
    await claimLab.searchAutocompleteStore(props.refDataModel!, term)
  } finally {
    pending.value--
  }
}

const searchTerm = ref('')

const search = useDebounceFn(searchStore, 600)

watch(searchTerm, (term) => {
  if (props.refDataModel && term) search(term)
})

const onUpdate = (values: string[]) => {
  state[props.name] = values.slice(0, props.maxEntries).join(',')
}

/* A created term may hold several comma-separated codes; entries stay unique */
const onCreate = (term: string) => {
  const created = term.split(',').map((code) => code.trim())

  onUpdate([...new Set([...entries.value, ...created.filter(Boolean)])])
  searchTerm.value = ''
}
</script>

<template>
  <FormFieldCell :name="props.name">
    <UInputMenu
      v-model:search-term="searchTerm"
      v-bind="attributes"
      :name="props.name"
      class="w-full"
      :items="items"
      :loading="loading"
      :model-value="entries"
      :ui="{ base: 'p-2 rounded-none' }"
      create-item="always"
      label-key="value"
      multiple
      open-on-focus
      size="xs"
      trailing
      value-key="value"
      variant="none"
      virtualize
      data-testid="FormListEditor__input"
      @create="onCreate"
      @update:model-value="onUpdate"
    >
      <template #empty="{ searchTerm: term }">
        {{ term ? 'No results' : 'Type to search' }}
      </template>
    </UInputMenu>
  </FormFieldCell>
</template>
